import { cn } from "@/lib/cn";
import { forwardRef } from "react";
import Icon from "../Icon/Icon";
import type { IconName } from "../Icon/iconMap";
import HStack from "../Layout/HStack";

interface TypeConfig {
  [key: string]: {
    iconName?: IconName;
    textColor: string;
    backgroundColor?: string;
    borderColor?: string;
    rounded?: string;
  };
}

const typeConfig: TypeConfig = {
  errorCard: {
    iconName: "InfoIcon",
    textColor: "text-error-on-neutral-fill",
    backgroundColor: "bg-error-muted-fill",
    rounded: "rounded-lg",
  },
  error: {
    textColor: "text-error-on-neutral-fill",
    borderColor: undefined,
    rounded: "rounded-lg",
  },
  errorBalloon: {
    textColor: "text-error-on-muted-fill",
    borderColor: undefined,
    backgroundColor: "bg-error-muted-fill",
    rounded: "rounded-lg rounded-tl-none",
  },
};

type TypeConfigKeys = "error" | "errorCard" | "errorBalloon";

const ResponseMessage = forwardRef<
  HTMLDivElement,
  JSX.IntrinsicElements["div"] & {
    type?: TypeConfigKeys;
    className?: string;
    size?: "xs" | "sm" | "md";
  }
>(function ResponseMessage(
  { children, type = "error", className = "", size = "sm", ...props },
  ref,
) {
  const { iconName, textColor, backgroundColor, borderColor, rounded } =
    typeConfig[type] || typeConfig.error;

  const containerClassName = cn(
    `${`${backgroundColor} ${borderColor} ${rounded}`} items-center gap-3 p-2`,
    className,
  );

  return children ? (
    <>
      {type === "errorCard" && (
        <div className="mt-1">
          <HStack ref={ref} className={containerClassName} {...props}>
            <Icon
              iconName={iconName ?? "InfoIcon"}
              size={24}
              colorVariant="error"
            />
            <p className={cn(`text-${size} font-semibold`, textColor)}>
              {children}
            </p>
          </HStack>
        </div>
      )}

      {type === "error" && (
        <div className="mt-1">
          <HStack ref={ref} className={containerClassName} {...props}>
            <p className={cn(`text-${size} font-semibold`, textColor)}>
              {children}
            </p>
          </HStack>
        </div>
      )}
      {type === "errorBalloon" && (
        <div className="mt-2 ml-3">
          <HStack ref={ref} className={containerClassName} {...props}>
            <p className={cn(`text-${size} font-normal`, textColor)}>
              {children}
            </p>
          </HStack>
        </div>
      )}
    </>
  ) : null;
});

export default ResponseMessage;
